import { useLocation, useSearchParams } from 'react-router-dom'
import { routes } from '../../routes/rutas'
import { CBreadcrumb, CBreadcrumbItem } from '@coreui/react'
import Icon from 'components/icon/Icon'
import { useResponsive } from 'hooks/useResponsive'
import { buildNavigationData, getNavContext } from 'helpers/navContext'
import { encrypt } from 'helpers/decrypt'
import './AppBreadcrumb.scss'

export const AppBreadcrumb = () => {
    const currentLocation = useLocation().pathname
    const [searchParams] = useSearchParams()
    const { isMobile } = useResponsive()

    const getRouteName = (pathname: any) => {
        const currentRoute = routes.find((route) => route.path === pathname)
        return currentRoute ? currentRoute.name : false
    }

    const getBreadcrumbs = (location: string) => {
        const breadcrumbs: any = []
        const navigationData = buildNavigationData(location, searchParams)

        const urlParams = searchParams.get('data');
        let urlPayload: any = null;
        if (urlParams) {
            try {
                const decryptModule = require('../helpers/decrypt');
                const decrypt = decryptModule.decrypt || decryptModule.default?.decrypt || decryptModule.default;
                if (decrypt && typeof decrypt === 'function') {
                    urlPayload = JSON.parse(decrypt(urlParams));
                }
            } catch (_) {}
        }

        location.split('/').reduce((prev: string, curr: string, index: number, array: string[]) => {
            const currentPathname = `${prev}/${curr}`
            const routeName = getRouteName(currentPathname)

            if (routeName) {
                let urlParam = ''

                if (navigationData) {
                    if (currentPathname.includes('/facilitador/ciclos/modulos/sesion/entregas')) {
                        if (navigationData.idTareaSesion) {
                            urlParam = `?data=${encrypt({ idTareaSesion: navigationData.idTareaSesion })}`
                        }
                    } else if (currentPathname.includes('/facilitador/ciclos/modulos/sesion')) {
                        if (navigationData.idGrupoSesion && navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: navigationData.idCiclo,
                                idGrupoSesion: navigationData.idGrupoSesion
                            })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado cuando no está en navigationData
                            const grupoSesionCtx = getNavContext('facil_grupoSesion');
                            if (grupoSesionCtx?.idGrupoSesion && grupoSesionCtx?.idCiclo) {
                                urlParam = `?data=${encrypt({
                                    idCiclo: grupoSesionCtx.idCiclo,
                                    idGrupoSesion: grupoSesionCtx.idGrupoSesion
                                })}`
                            }
                        }
                    } else if (currentPathname.includes('/facilitador/ciclos/modulos')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado
                            const cicloCtx = getNavContext('facil_ciclo');
                            const grupoSesionCtx = getNavContext('facil_grupoSesion');
                            const idCiclo = cicloCtx?.idCiclo || grupoSesionCtx?.idCiclo;
                            if (idCiclo) {
                                urlParam = `?data=${encrypt({ idCiclo })}`
                            }
                        }
                    } else if (currentPathname.includes('/facilitador/ciclos')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado
                            const cicloCtx = getNavContext('facil_ciclo');
                            if (cicloCtx?.idCiclo) {
                                urlParam = `?data=${encrypt({ idCiclo: cicloCtx.idCiclo })}`
                            }
                        }
                    } else if (currentPathname.includes('/facilitador/control-academico/alumno')) {
                        const idGrupo = navigationData.idGrupo;
                        const idUsuarioAlumno = navigationData.idUsuarioAlumno || navigationData.idUsuario;
                        if (idGrupo && idUsuarioAlumno) {
                            urlParam = `?data=${encrypt({ idGrupo, idUsuarioAlumno })}`
                        } else {
                            const grupoCtx = getNavContext('facil_grupo');
                            const alumnoCtx = getNavContext('facil_alumno');
                            const grupoFallback = idGrupo || grupoCtx?.idGrupo;
                            const alumnoFallback = idUsuarioAlumno || alumnoCtx?.idUsuarioAlumno;
                            if (grupoFallback && alumnoFallback) {
                                urlParam = `?data=${encrypt({ idGrupo: grupoFallback, idUsuarioAlumno: alumnoFallback })}`
                            }
                        }
                    } else if (currentPathname.includes('/facilitador/control-academico/grupo')) {
                        if (navigationData.idGrupo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: navigationData.idCiclo,
                                idGrupo: navigationData.idGrupo
                            })}`
                        }
                    } else if (currentPathname.includes('/facilitador/control-academico/modulo/resultados')) {
                        if (navigationData.idModulo && navigationData.idGrupo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: navigationData.idCiclo,
                                idModulo: navigationData.idModulo,
                                idGrupo: navigationData.idGrupo
                            })}`
                        }
                    } else if (currentPathname.includes('/facilitador/control-academico/resultados')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        }
                    } else if (currentPathname.includes('/facilitador/control-academico/ciclo')) {
                        if (navigationData.idCiclo) {
                            const payload: any = { idCiclo: navigationData.idCiclo };
                            if (navigationData.idGrupo) payload.idGrupo = navigationData.idGrupo;
                            urlParam = `?data=${encrypt(payload)}`
                        }
                    } else if (currentPathname.includes('/alumno/actividades/entrega') && navigationData.idTareaAlumno) {
                        if (navigationData.idTareaAlumno) {
                            urlParam = `?data=${encrypt({ idTareaAlumno: navigationData.idTareaAlumno })}`
                        }
                    } else if (currentPathname.includes('/alumno/actividades/entrega')) {
                        if (navigationData.idTareaSesion) {
                            urlParam = `?data=${encrypt({ idTareaSesion: navigationData.idTareaSesion })}`
                        }
                    } else if (currentPathname.includes('/alumno/evaluaciones')) {
                        if (navigationData.idEvaluacion && navigationData.esEncuesta === false) {
                            urlParam = `?data=${encrypt({
                                idEvaluacion: navigationData.idEvaluacion,
                                idEvaluacionIntento: navigationData.idEvaluacionIntento,
                                esEncuesta: false,
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        } else {
                            urlParam = `?data=${encrypt({
                                esEncuesta: false,
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        }
                    } else if (currentPathname.includes('/alumno/encuestas')) {
                        if (navigationData.idEvaluacion && navigationData.esEncuesta) {
                            urlParam = `?data=${encrypt({
                                idEvaluacion: navigationData.idEvaluacion,
                                idEvaluacionIntento: navigationData.idEvaluacionIntento,
                                esEncuesta: true,
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        } else {
                            urlParam = `?data=${encrypt({
                                esEncuesta: true,
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        }
                    } else if (currentPathname.includes('/alumno/ciclos/modulos/sesiones/detalle')) {
                        if (navigationData.idGrupoSesion && navigationData.idCiclo && navigationData.idModulo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: navigationData.idCiclo,
                                idModulo: navigationData.idModulo,
                                idGrupoSesion: navigationData.idGrupoSesion
                            })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado
                            const sesionCtx = getNavContext('alumno_sesion');
                            if (sesionCtx?.idGrupoSesion && sesionCtx?.idCiclo && sesionCtx?.idModulo) {
                                urlParam = `?data=${encrypt({
                                    idCiclo: sesionCtx.idCiclo,
                                    idModulo: sesionCtx.idModulo,
                                    idGrupoSesion: sesionCtx.idGrupoSesion
                                })}`
                            }
                        }
                    } else if (currentPathname === '/lideresaccion/alumno/ciclos/modulos/sesiones') {
                        if (urlPayload?.idModulo && urlPayload?.idCiclo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: urlPayload.idCiclo,
                                idModulo: urlPayload.idModulo,
                                idGrupo: urlPayload.idGrupo
                            })}`
                        } else if (navigationData?.idModulo && navigationData?.idCiclo) {
                            urlParam = `?data=${encrypt({
                                idCiclo: navigationData.idCiclo,
                                idModulo: navigationData.idModulo,
                                idGrupo: navigationData.idGrupo
                            })}`
                        } else if (navigationData?.idModulo) {
                            const sesionCtx = getNavContext('alumno_sesion');
                            const moduloCtx = getNavContext('alumno_modulo');
                            const cicloCtx = getNavContext('alumno_ciclo');

                            const idCiclo = navigationData.idCiclo || sesionCtx?.idCiclo || moduloCtx?.idCiclo || cicloCtx?.idCiclo;
                            const idModulo = navigationData.idModulo;
                            const idGrupo = navigationData.idGrupo || sesionCtx?.idGrupo;

                            if (idCiclo && idModulo) {
                                urlParam = `?data=${encrypt({
                                    idCiclo,
                                    idModulo,
                                    idGrupo
                                })}`
                            }
                        }
                    } else if (currentPathname.includes('/alumno/ciclos/modulos')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado
                            const moduloCtx = getNavContext('alumno_modulo');
                            const cicloCtx = getNavContext('alumno_ciclo');
                            const idCiclo = moduloCtx?.idCiclo || cicloCtx?.idCiclo;
                            if (idCiclo) {
                                urlParam = `?data=${encrypt({ idCiclo })}`
                            }
                        }
                    } else if (currentPathname.includes('/alumno/ciclos')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        } else {
                            // Fallback: recuperar del contexto almacenado
                            const cicloCtx = getNavContext('alumno_ciclo');
                            if (cicloCtx?.idCiclo) {
                                urlParam = `?data=${encrypt({ idCiclo: cicloCtx.idCiclo })}`
                            }
                        }
                    } else if (currentPathname.includes('/administrador/diplomados/ciclos/grupos/modulos')) {
                        if (navigationData.idGrupo && navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({
                                idGrupo: navigationData.idGrupo,
                                idCiclo: navigationData.idCiclo
                            })}`
                        }
                    } else if (currentPathname.includes('/administrador/diplomados/ciclos/grupos')) {
                        if (navigationData.idCiclo) {
                            urlParam = `?data=${encrypt({ idCiclo: navigationData.idCiclo })}`
                        }
                    } else if (currentPathname.includes('/administrador/diplomados/ciclos')) {
                        if (navigationData.idDiplomado) {
                            urlParam = `?data=${encrypt({ idDiplomado: navigationData.idDiplomado })}`
                        }
                    } else if (currentPathname.includes('/administrador/diplomados')) {
                        if (navigationData.idDiplomado) {
                            urlParam = `?data=${encrypt({ idDiplomado: navigationData.idDiplomado })}`
                        }
                    } else if (currentPathname.includes('/facilitador/evaluaciones')) {
                        if (navigationData.idEvaluacion) {
                            urlParam = `?data=${encrypt({
                                idEvaluacion: navigationData.idEvaluacion,
                                idEvaluacionIntento: navigationData.idEvaluacionIntento,
                                esEncuesta: navigationData.esEncuesta || false,
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        } else {
                            urlParam = `?data=${encrypt({
                                _source: 'navigation',
                                applyFilters: false
                            })}`
                        }
                    }
                }

                if (!urlParam) {
                    const param = searchParams.get("data")
                    if (param && (index + 1 === array.length)) {
                        urlParam = `?data=${param}`
                    }
                }

                breadcrumbs.push({
                    pathname: `${currentPathname}${urlParam}`,
                    name: routeName,
                    active: (index + 1 === array.length && currentPathname === location) ? true : false,
                })
            }
            return currentPathname
        })
        return breadcrumbs
    }

    const breadcrumbs = getBreadcrumbs(currentLocation)

    if (breadcrumbs.length === 0) return null

    return (
        <div className="breadcrumb-container">
            <CBreadcrumb className="enhanced-breadcrumb">
                <CBreadcrumbItem
                    href="/lideresaccion/dashboard"
                    className="breadcrumb-home"
                >
                    <Icon icono="mdiHome" size={0.9} />
                    {!isMobile && <span className="home-text">Inicio</span>}
                </CBreadcrumbItem>

                {breadcrumbs.map((breadcrumb: any, index: any) => {
                    const isLast = index === breadcrumbs.length - 1
                    return (
                        <CBreadcrumbItem
                            {...(breadcrumb.active ? { active: true } : { href: breadcrumb.pathname })}
                            key={index}
                            className={`breadcrumb-item ${isLast ? 'breadcrumb-active' : 'breadcrumb-link'}`}
                        >
                            <span className="breadcrumb-text">
                                {isMobile && breadcrumb.name.length > 20
                                    ? `${breadcrumb.name.substring(0, 17)}...`
                                    : breadcrumb.name
                                }
                            </span>
                        </CBreadcrumbItem>
                    )
                })}
            </CBreadcrumb>
        </div>
    )
}
